import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, CalendarDays } from "lucide-react";
import { GuardOnDutyPanel } from "@/components/GuardOnDutyPanel";
import { GuardsCalendar } from "@/components/GuardsCalendar";
import { isOperationalRole } from "@/lib/roles";

export default function PassiveGuards() {
  const { user } = useAuth();
  const role = user?.role;
  const canManage = role === "admin" || role === "supervisor";
  const isMobile = isOperationalRole(role);
  const backLink = role === "admin" ? "/admin" : role === "supervisor" ? "/supervisor" : "/vigilador";

  const [onDuty, setOnDuty] = useState([]);
  const [myGuards, setMyGuards] = useState([]);

  const refresh = async () => {
    try {
      const reqs = [api.get("/passive-guards/on-duty")];
      if (isMobile) reqs.push(api.get("/passive-guards"));
      const res = await Promise.all(reqs);
      setOnDuty(res[0].data);
      if (isMobile) setMyGuards(res[1].data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const deleteGuard = async (id) => {
    try { await api.delete(`/passive-guards/${id}`); toast.success("Guardia eliminada"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const upcoming = myGuards.filter((g) => !g.on_duty_now);

  return (
    <div style={{ padding: isMobile ? "8px 16px" : "20px 32px", maxWidth: 1100, margin: "0 auto" }} data-testid="passive-guards-page">
      <Link to={backLink} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--invict-text-muted)", fontSize: 12, marginBottom: 12, textDecoration: "none", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}><ArrowLeft size={14}/> Volver</Link>
      <h1 style={{ fontSize: 26, fontWeight: 900, margin: "4px 0 4px", color: "#0A0A0A", letterSpacing: "-0.02em" }}>Guardias Pasivas (retén)</h1>
      <p style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 18 }}>
        {isMobile ? "Tus turnos de disponibilidad para relevo o apoyo ante emergencias." : "Disponibilidad de personal fuera de turno para relevo o apoyo ante emergencias."}
      </p>

      <GuardOnDutyPanel guards={onDuty} canManage={canManage} onDelete={deleteGuard} isMobile={isMobile} />

      {canManage && <GuardsCalendar onChanged={refresh} />}

      {isMobile && (
        <>
          <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#0A0A0A", marginBottom: 10 }}>Mis próximas guardias</div>
          <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="my-guards-list">
            {upcoming.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "var(--invict-text-muted)", fontSize: 13 }} data-testid="my-guards-empty">Sin guardias programadas.</div>}
            {upcoming.map((g) => (
              <div key={g.id} style={{ padding: "12px 16px", borderBottom: "1px solid var(--invict-bone-3)", display: "flex", gap: 12, alignItems: "center" }} data-testid={`my-guard-row-${g.id}`}>
                <span style={{ background: "var(--invict-bone-3)", color: "#0A0A0A", borderRadius: "50%", width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 900, flexShrink: 0 }}>{g.priority}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#0A0A0A", display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                    <CalendarDays size={12} />
                    {(g.dates && g.dates.length) ? g.dates.join(" · ") : `${g.date_from} → ${g.date_to}`}
                    {g.time_from && g.time_to ? ` · ${g.time_from}–${g.time_to}` : ""}
                  </div>
                  {g.note && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", fontStyle: "italic" }}>{g.note}</div>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
